/**
 * Conversation Analysis (SA-212) — admin page: run builder + polling runs
 * list over the SA-211 analysis-run API
 * (backend/app/routers/conversation_analysis.py). The report viewer lives
 * at /admin/conversation-analysis/runs/:runId (AnalysisRunPage).
 */
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LineChart, Trash2 } from 'lucide-react'

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { useCanAccessFeature } from '@/hooks'
import { usePermission } from '@/hooks/usePermission'
import {
  useAnalysisRuns,
  useAnalysisUserNames,
  useDeleteAnalysisRun,
} from '@/hooks/useConversationAnalysis'
import { RunBuilder } from './RunBuilder'
import { RunStatusChip } from './RunStatusChip'
import { TrendsTab } from './TrendsTab'
import {
  analysisErrorMessage, displayUserName, formatCost, formatFilterSummary,
  reportTypeLabel,
} from './runFormat'
import type { AnalysisRunSummary } from '@/types/conversationAnalysis'

export function ConversationAnalysisPage() {
  const canAccess = useCanAccessFeature('conversation_analytics', 'conversation_analysis:read')
  const runs = useAnalysisRuns(canAccess)
  const userNames = useAnalysisUserNames(canAccess)
  // SA-237: the report page's Investigate button navigates here with the
  // failure-cluster ids; the key remounts the builder on a fresh prefill.
  const location = useLocation()
  const deepDiveIds = (location.state as { deepDiveIds?: string[] } | null)
    ?.deepDiveIds

  // Server enforces conversation_analysis:run for DELETE — mirror it here.
  const canRun = usePermission('conversation_analysis:run')
  const deleteRun = useDeleteAnalysisRun()
  // SA-190 idiom: per-action inline errors, keyed by run id.
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({})

  const handleDelete = (run: AnalysisRunSummary) => {
    if (!window.confirm(`Delete this ${run.reportType} report? This cannot be undone.`)) return
    setActionErrors((prev) => ({ ...prev, [run.id]: '' }))
    deleteRun.mutate(run.id, {
      onError: (e) =>
        setActionErrors((prev) => ({
          ...prev,
          [run.id]: `Delete failed — ${analysisErrorMessage(e)}`,
        })),
    })
  }

  if (!canAccess) {
    return (
      <PageFrame width="form" className="py-8">
        <p className="text-sm text-muted-foreground">
          You don't have access to Conversation Analysis.
        </p>
      </PageFrame>
    )
  }

  return (
    <PageFrame width="detail" className="space-y-4">
      <PageHeader
        title="Conversation Analysis"
        icon={LineChart}
        sticky
        subtitle="LLM-generated quality reports over stored conversations. Reports recommend only — nothing is applied automatically."
      />

      <Tabs defaultValue="runs">
        <TabsList>
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>
        <TabsContent value="runs" className="space-y-4">
          <RunBuilder
            key={deepDiveIds?.join(',') ?? 'default'}
            initialDeepDiveIds={deepDiveIds}
          />

          {runs.isLoading && (
            <p className="py-8 text-sm text-muted-foreground">Loading runs…</p>
          )}
          {runs.isError && (
            <p role="alert" className="py-8 text-sm text-destructive">
              Couldn't load runs — {analysisErrorMessage(runs.error)}
            </p>
          )}
          {runs.isSuccess && runs.data.length === 0 && (
            <div className="py-12 text-center">
              <LineChart className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">
                No analysis runs yet{canRun ? ' — build one above.' : '.'}
              </p>
            </div>
          )}

          {runs.isSuccess && runs.data.length > 0 && (
            <ul className="divide-y divide-border rounded-sm border border-border bg-card">
              {runs.data.map((run) => (
                <li key={run.id} className="px-4 py-3 space-y-1">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/admin/conversation-analysis/runs/${run.id}`}
                        className="text-sm font-medium text-foreground hover:underline capitalize"
                      >
                        {reportTypeLabel(run.reportType)} report
                      </Link>
                      <p className="text-xs text-muted-foreground truncate">
                        {formatFilterSummary(run.filters)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {displayUserName(run.createdBy, userNames.data)}
                        {run.createdAt && ` · ${new Date(run.createdAt).toLocaleString()}`}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {run.analyzedCount < run.conversationCount
                        ? `${run.analyzedCount} of ${run.conversationCount} conversations`
                        : `${run.conversationCount} conversation${run.conversationCount === 1 ? '' : 's'}`}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatCost(run.totalCostUsd)}
                    </span>
                    <RunStatusChip status={run.status} />
                    {canRun && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleDelete(run)}
                        disabled={deleteRun.isPending}
                        className="text-xs text-destructive hover:text-destructive/80"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                  {run.status === 'error' && run.error && (
                    <p className="text-xs text-destructive">{run.error}</p>
                  )}
                  {actionErrors[run.id] && (
                    <p role="alert" className="text-xs text-destructive">{actionErrors[run.id]}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
        <TabsContent value="trends">
          <TrendsTab />
        </TabsContent>
      </Tabs>
    </PageFrame>
  )
}
